import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import React, { useId, useMemo } from 'react'

import { SolidAlertTriangleIcon } from './icons/SolidAlertTriangle'
import { type IconProp } from './types'

export type TextLikeFormFieldChildrenProps = {
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'false' | 'true' | 'grammar' | 'spelling';
  className?: string;
  id?: string;
  required?: boolean;
}

export type TextLikeFormFieldProps = {
  children: (formFieldProps: TextLikeFormFieldChildrenProps) => React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  id?: string;
  label?: React.ReactNode;
  leadingIcon?: IconProp;
  required?: boolean;
}

interface ClearButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export function ClearButton ({ onClick }: ClearButtonProps) {
  return (
    <button
      aria-label='clear value'
      type='button'
      className='appearance-none rounded-sm p-1 cursor-pointer text-gray-500 hover:text-gray-700 active:text-gray-800 outline-none focus-visible:ring ring-transparent focus-visible:ring-thatch-500 opacity-0 group-hover/field:opacity-100 group-has-[.field:focus]/field:opacity-100 transition'
      onClick={onClick}
      /**
       * We're following the pattern established by the native HTML
       * search input, where the clear button is only mouse operable.
       * Keyboard users already have an easy way to clear the input:
       * the text is selected when the input is focused so they only
       * need to hit `Backspace` or `Delete`.
       */
      tabIndex={-1}
    >
      <XIcon size={16} />
    </button>
  )
}

export function TextLikeFormField ({
  children,
  description,
  id,
  error,
  label,
  leadingIcon: LeadingIcon,
  required,
  ...props
}: TextLikeFormFieldProps) {
  const autogeneratedId = useId()
  const descriptionId = useId()
  const errorMessageId = useId()
  const inputId = id || autogeneratedId

  const displayDescription = !!description

  const describedBy = useMemo(
    () =>
      [
        displayDescription && descriptionId,
        error && errorMessageId,
      ]
        .filter(Boolean)
        .join(' '),
    [error, displayDescription]
  )

  function renderDescription () {
    if (!displayDescription) return null

    return (
      <div className='body-sm text-gray-500 mt-1' id={descriptionId}>
        {description}
      </div>
    )
  }

  function renderErrorMessage () {
    if (!error || typeof error === 'boolean') return null

    return (
      <div className='flex gap-x-1 mt-1' id={errorMessageId}>
        <div className='text-red-400'>
          <SolidAlertTriangleIcon size={16} />
        </div>
        <div className='body-sm-bold text-red-700 '>
          {error}
        </div>
      </div>
    )
  }

  function renderLabel () {
    if (!label) return null

    return (
      <div className='mb-2'>
        <label
          aria-required={required}
          className={clsx('body-sm-bold block', {
            'text-red-700': error,
            'text-gray-800': !error,
          })}
          htmlFor={inputId}
        >
          {label}
          {required ? '*' : ''}
        </label>
      </div>
    )
  }

  function renderLeadingIcon () {
    if (!LeadingIcon) return null

    return (
      <div
        data-testid='leading-icon'
        className={clsx(
          'pointer-events-none',
          'absolute left-2 top-0 bottom-0',
          'flex items-center',
          'text-gray-500 hover:text-gray-600 group-has-[.field:disabled]/field:text-black/25'
        )}
      >
        <LeadingIcon size={16} />
      </div>
    )
  }

  function renderChildren () {
    if (typeof children !== 'function') return null

    const className = clsx(
      'field',
      'w-full p-2',
      'appearance-none bg-none opacity-100', // bg-none and opacity-100 remove some global styles on select elements
      'body',
      'leading-[1.375rem]',
      'border-none hover:border-none bg-transparent',
      'placeholder:text-gray-500 data-[placeholder="true"]:text-gray-500 data-[placeholder="true"]:focus:text-gray-500 data-[placeholder="true"]:aria-invalid:text-gray-500 data-[placeholder="true"]:disabled:text-gray-500',
      'text-gray-800 focus:text-gray-800',
      'invalid:text-red-700 aria-invalid:text-red-700 focus:invalid:text-gray-800 focus:aria-invalid:text-gray-800',
      'disabled:text-gray-800 disabled:placeholder:text-black/25',
      'focus:outline-hidden focus:ring-transparent',
      'transition-colors',
      {
        'pl-8': LeadingIcon,
      }
    )

    const childrenProps: TextLikeFormFieldChildrenProps = {
      ...props,
      'aria-describedby': describedBy,
      'aria-invalid': !!error,
      className,
      id: inputId,
      required,
    }

    return children(childrenProps)
  }

  return (
    <div>
      {renderLabel()}
      <div
        data-form-field-for={inputId}
        className={clsx(
          'relative',
          'group/field',
          'rounded-lg has-[[data-popper-side="bottom"][aria-expanded="true"]]:rounded-b-none has-[[data-popper-side="top"][aria-expanded="true"]]:rounded-t-none',
          'transition-[background,border,color,box-shadow,outline]',
          'shadow-sm',
          'border border-gray-400 has-[[aria-invalid="true"]]:border-red-300 has-[:invalid]:border-red-300 focus-within:border-thatch-500 has-[:invalid]:focus-within:border-thatch-500 has-[[aria-invalid="true"]]:focus-within:border-thatch-500 has-[.field:disabled]:border-gray-100',
          'bg-white hover:bg-gray-50 has-[:focus]:bg-thatch-50 has-[.field:disabled]:bg-gray-100 has-[.field:disabled]:hover:bg-gray-100',
          'ring-inset ring-1 ring-transparent has-[.field:focus]:ring-thatch-500'
        )}
      >
        {renderLeadingIcon()}
        {renderChildren()}
      </div>
      {renderErrorMessage()}
      {renderDescription()}
    </div>
  )
}
